import {EmptyPolicyNumber, PolicyNumber, ValidPolicyNumber} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {
  CriticalIllnessPlans,
  isLoaded as isLoadedPlans,
  UnassignedCriticalIllnessPlans,
} from 'ApplicationEntry/models/ProductsRiders/CriticalIllness/CIPlanTypes';
import {
  CriticalIllnessSmokingCodes,
  isLoaded as isLoadedSmokerCode,
  UnassignedCISmokingCodes,
} from 'ApplicationEntry/models/ProductsRiders/CriticalIllness/SmokerCodes';
import {
  isLoaded as isLoadedProducts,
  Plan,
  PlanModel,
  UnassignedPlanModel,
} from 'ApplicationEntry/models/ProductsRiders/PlanModel';
import {Loaded} from 'Core/models/Async';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import * as I from 'immutable';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import * as RI from '../RiderInformation';

type CriticalIllnessProps = {
  productType: StringUnion;
  planType: StringUnion;
  benefitAmount: number;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  cwa: StringUnion;
  insuredSC: StringUnion;
  spouseSC: StringUnion;
  section125: boolean;
  replacement: boolean;
  policyNumber: PolicyNumber;
  productList: PlanModel;
  plantypes: CriticalIllnessPlans;
  smokerCodes: CriticalIllnessSmokingCodes;
  cicr1Rider: boolean;
  riderInformation: RI.RiderInformation;
};

export class InvalidOrdCriticalIllness {
  public readonly key = 'InvalidOrdCriticalIllness';

  constructor(
    public readonly productType: StringUnion,
    public readonly planType: StringUnion,
    public readonly benefitAmount: number,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly insuredSC: StringUnion,
    public readonly spouseSC: StringUnion,
    public readonly section125: boolean,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly productList: PlanModel,
    public readonly plantypes: CriticalIllnessPlans,
    public readonly smokerCodes: CriticalIllnessSmokingCodes,
    public readonly cicr1Rider: boolean,
    public readonly riderInformation: RI.RiderInformation
  ) {}
}

export class ValidOrdinaryCriticalIllness {
  public static createApp({
    productType,
    planType,
    benefitAmount,
    premium,
    calculatedPremium,
    cwa,
    insuredSC,
    spouseSC,
    section125,
    replacement,
    policyNumber,
    productList,
    plantypes,
    smokerCodes,
    cicr1Rider,
    riderInformation,
  }: CriticalIllnessProps) {
    return this.create(
      productType,
      planType,
      benefitAmount,
      premium,
      calculatedPremium,
      cwa,
      insuredSC,
      spouseSC,
      section125,
      replacement,
      policyNumber,
      productList,
      plantypes,
      smokerCodes,
      cicr1Rider,
      riderInformation
    );
  }

  public static create(
    productType: StringUnion,
    planType: StringUnion,
    benefitAmount: number,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    cwa: StringUnion,
    insuredSC: StringUnion,
    spouseSC: StringUnion,
    section125: boolean,
    replacement: boolean,
    policyNumber: PolicyNumber,
    productList: PlanModel,
    plantypes: CriticalIllnessPlans,
    smokerCodes: CriticalIllnessSmokingCodes,
    cicr1Rider: boolean,
    riderInformation: RI.RiderInformation
  ) {
    return isNonEmptyString(productType) &&
      planType &&
      benefitAmount &&
      isNonEmptyString(premium) &&
      isNonEmptyString(cwa) &&
      insuredSC &&
      section125 &&
      replacement &&
      policyNumber &&
      RI.isLoaded(riderInformation) &&
      riderInformation.Data.codes.size > 0 &&
      riderInformation.Data.amounts.size > 0 &&
      isLoadedProducts(productList) &&
      isLoadedPlans(plantypes) &&
      isLoadedSmokerCode(smokerCodes)
      ? new ValidOrdinaryCriticalIllness(
          productType,
          planType,
          benefitAmount,
          premium,
          calculatedPremium,
          cwa,
          insuredSC,
          spouseSC,
          section125,
          replacement,
          policyNumber,
          productList,
          plantypes,
          smokerCodes,
          cicr1Rider,
          riderInformation
        )
      : new InvalidOrdCriticalIllness(
          productType,
          planType,
          benefitAmount,
          premium,
          calculatedPremium,
          cwa,
          insuredSC,
          spouseSC,
          section125,
          replacement,
          policyNumber,
          productList,
          plantypes,
          smokerCodes,
          cicr1Rider,
          riderInformation
        );
  }

  public readonly key = 'ValidOrdCriticalIllness';
  constructor(
    public readonly productType: StringUnion,
    public readonly planType: StringUnion,
    public readonly benefitAmount: number,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly insuredSC: StringUnion,
    public readonly spouseSC: StringUnion,
    public readonly section125: boolean,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly productList: Loaded<Plan[]>,
    public readonly plantypes: Loaded<string[]>,
    public readonly smokerCodes: Loaded<string[]>,
    public readonly cicr1Rider: boolean,
    public readonly riderInformation: Loaded<RI.default>
  ) {}
}

export type OrdinaryCriticalIllness = InvalidOrdCriticalIllness | ValidOrdinaryCriticalIllness;

export type OrdCriticalIllnessOptions<TReturn> = ToPartialMatchOptions<OrdinaryCriticalIllness, 'key', TReturn>;

export const matchOrdCriticalIllness = <TReturn>(options: OrdCriticalIllnessOptions<TReturn>) => (
  app: OrdinaryCriticalIllness
): TReturn => {
  switch (app.key) {
    case 'InvalidOrdCriticalIllness':
      return options.InvalidOrdCriticalIllness ? options.InvalidOrdCriticalIllness(app) : options.default(app);
    case 'ValidOrdCriticalIllness':
      return options.ValidOrdCriticalIllness ? options.ValidOrdCriticalIllness(app) : options.default(app);
  }
};

export const EmptyOrdCriticalIllness = new InvalidOrdCriticalIllness(
  new EmptyString(),
  new EmptyString(),
  0,
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  EmptyPolicyNumber,
  new UnassignedPlanModel(),
  new UnassignedCriticalIllnessPlans(),
  new UnassignedCISmokingCodes(),
  false,
  new RI.UnassignedRiderInformation()
);

export const changeProductType = (productType: string) => (app: OrdinaryCriticalIllness) =>
  ValidOrdinaryCriticalIllness.createApp({
    ...app,
    productType: NonEmptyString.create(productType),
    calculatedPremium: new EmptyString(),
    premium: new EmptyString(),
    cwa: new EmptyString(),
    benefitAmount: 0,
    policyNumber: EmptyPolicyNumber,
  });

export const changePlanType = (planType: string) => (app: OrdinaryCriticalIllness) =>
  ValidOrdinaryCriticalIllness.createApp({
    ...app,
    planType: NonEmptyString.create(planType),
    calculatedPremium: new EmptyString(),
    premium: new EmptyString(),
    cwa: new EmptyString(),
    benefitAmount: 0,
    policyNumber: EmptyPolicyNumber,
  });

export const changeBenefitAmount = (benefitAmount: number) => (app: OrdinaryCriticalIllness) =>
  ValidOrdinaryCriticalIllness.createApp({...app, benefitAmount});

export const changePremium = (premium: string) => (app: OrdinaryCriticalIllness) =>
  ValidOrdinaryCriticalIllness.createApp({...app, premium: NonEmptyString.create(premium)});

export const changeCalculatedPremium = (calculatedPremium: string) => (app: OrdinaryCriticalIllness) =>
  ValidOrdinaryCriticalIllness.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});

export const changeCWA = (cwa: string) => (app: OrdinaryCriticalIllness) =>
  ValidOrdinaryCriticalIllness.createApp({...app, cwa: NonEmptyString.create(cwa)});

export const changeInsuredSC = (insuredSC: string) => (app: OrdinaryCriticalIllness) =>
  ValidOrdinaryCriticalIllness.createApp({...app, insuredSC: NonEmptyString.create(insuredSC)});

export const changeSpouseSC = (spouseSC: string) => (app: OrdinaryCriticalIllness) =>
  ValidOrdinaryCriticalIllness.createApp({...app, spouseSC: NonEmptyString.create(spouseSC)});

export const changeSection125 = (section125: boolean) => (app: OrdinaryCriticalIllness) =>
  ValidOrdinaryCriticalIllness.createApp({...app, section125});

export const changeReplacement = (replacement: boolean) => (app: OrdinaryCriticalIllness) =>
  ValidOrdinaryCriticalIllness.createApp({
    ...app,
    replacement,
    policyNumber: ValidPolicyNumber.create(new EmptyString()),
  });

export const changeCICR1Rider = (cicr1Rider: boolean) => (app: OrdinaryCriticalIllness) =>
  ValidOrdinaryCriticalIllness.createApp({...app, cicr1Rider});

export const changePolicyNumber = (policyNumber: StringUnion) => (app: OrdinaryCriticalIllness) =>
  ValidOrdinaryCriticalIllness.createApp({...app, policyNumber: ValidPolicyNumber.create(policyNumber)});

export const changeProductList = (products: PlanModel) => (app: OrdinaryCriticalIllness) =>
  ValidOrdinaryCriticalIllness.createApp({...app, productList: products});

export const changePlanTypes = (plans: CriticalIllnessPlans) => (app: OrdinaryCriticalIllness) =>
  ValidOrdinaryCriticalIllness.createApp({...app, plantypes: plans});

export const changeSmokingCodes = (smokerCodes: CriticalIllnessSmokingCodes) => (app: OrdinaryCriticalIllness) =>
  ValidOrdinaryCriticalIllness.createApp({...app, smokerCodes});

export const removeRider = (index: number) => (app: OrdinaryCriticalIllness) => {
  const existingListAmounts: I.List<number> = RI.isLoaded(app.riderInformation)
    ? app.riderInformation.Data.amounts
    : I.List<number>();
  const newListAmounts = existingListAmounts.delete(index);
  const existingListCodes: I.List<string> = RI.isLoaded(app.riderInformation)
    ? app.riderInformation.Data.codes
    : I.List<string>();
  const newListCodes = existingListCodes.delete(index);

  const riderCodes: RI.RiderCode[] = RI.isLoaded(app.riderInformation)
    ? app.riderInformation.Data.riderData
    : ([] as RI.RiderCode[]);

  return ValidOrdinaryCriticalIllness.createApp({
    ...app,
    riderInformation: new Loaded(new RI.default(newListCodes, newListAmounts, riderCodes)),
  });
};

export const changeRiderInformationData = (riderAmount: number, riderCode: string, index: number) => (
  app: OrdinaryCriticalIllness
) => {
  const riderInformation = RI.default.changeRiderInformation(
    riderAmount,
    riderCode,
    index,
    RI.isLoaded(app.riderInformation) ? app.riderInformation.Data.amounts : I.List<number>(),
    RI.isLoaded(app.riderInformation) ? app.riderInformation.Data.codes : I.List<string>()
  );

  const riderCodes: RI.RiderCode[] = RI.isLoaded(app.riderInformation)
    ? app.riderInformation.Data.riderData
    : ([] as RI.RiderCode[]);

  return ValidOrdinaryCriticalIllness.createApp({
    ...app,
    riderInformation: new Loaded(
      new RI.default(riderInformation.riderCodes, riderInformation.riderAmounts, riderCodes)
    ),
  });
};

export const changeRiderInformation = (riderInformation: RI.RiderInformation) => (app: OrdinaryCriticalIllness) =>
  ValidOrdinaryCriticalIllness.createApp({
    ...app,
    riderInformation,
  });

export const disjointCodeSet = ['WBR', 'WBR3'];
